import { DATE_PIPE_DEFAULT_OPTIONS, DATE_PIPE_DEFAULT_TIMEZONE, DatePipe, DatePipeConfig } from '@angular/common';
import { Inject, LOCALE_ID, Optional, Pipe, PipeTransform } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { selectors } from '@twaice-fe/frontend/shared/store';
import { CustomUserTimezone } from '@twaice-fe/shared/models';
import { injectStorageTimezone } from './storage-timezone';

const { authSelectors } = selectors;

@Pipe({
  name: 'twDate',
  standalone: true,
})
/**
 * Drop-in replacement for Angular DatePipe that adds user-selected timezone awareness.
 *
 * This pipe automatically transforms dates into the user-selected timezone, taking care of
 * arbitrary timezones, local browser timezone and storage-specific timezone
 *
 * !! Note: THIS PIPE IS SYNCHRONOUS !!
 *
 * Dates are transformed once, given the current user choice and system context!
 * If the system or user timezone changes, dates remain unchanged until the next change detection cycle.
 * If you need to show a date that persists system changes, you need to take care of change detection yourself.
 */
export class TimezoneAwareDatePipe extends DatePipe implements PipeTransform {
  private selectedTimezone?: string | null = null;
  private systemTimezone?: string | null = null;

  constructor(
    private store: Store,
    @Inject(LOCALE_ID) locale: string,
    @Inject(DATE_PIPE_DEFAULT_TIMEZONE) @Optional() defaultTimezone?: string | null,
    @Inject(DATE_PIPE_DEFAULT_OPTIONS) @Optional() defaultOptions?: DatePipeConfig | null
  ) {
    super(locale, defaultTimezone, defaultOptions);

    this.store
      .select(authSelectors.getUserTimezone)
      .pipe(takeUntilDestroyed())
      .subscribe((timezone) => {
        if (timezone) {
          this.selectedTimezone = timezone;
        }
      });

    injectStorageTimezone()
      .pipe(takeUntilDestroyed())
      .subscribe((timezone) => {
        if (timezone) {
          this.systemTimezone = timezone;
        }
      });
  }

  /**
   * @description
   * Use this as you would DatePipe.transform().
   * You can override the timezone by passing it as the third parameter, also supporting "STORAGE_TIME"
   *
   * @param value - The date to transform
   * @param format - The format to use
   * @param timezone - The timezone to use
   * @param locale - The locale to use
   *
   */
  override transform(value: Date | string | number, format?: string, timezone?: string, locale?: string): string;
  override transform(value: null | undefined, format?: string, timezone?: string, locale?: string): null;
  override transform(
    value: Date | string | number | null | undefined,
    format?: string,
    timezone?: string,
    locale?: string
  ): string | null;
  override transform(
    value: Date | string | number | null | undefined,
    format?: string,
    timezone?: string,
    locale?: string
  ): string | null {
    let selectedTimezone;

    switch (this.selectedTimezone) {
      case CustomUserTimezone.STORAGE_TIME:
        selectedTimezone = this.systemTimezone;
        break;
      case CustomUserTimezone.BROWSER_TIME:
        selectedTimezone = timezone;
        break;
      case CustomUserTimezone.UTC:
        selectedTimezone = 'UTC';
        break;
      default:
        selectedTimezone = this.selectedTimezone || timezone;
        break;
    }

    // respect manual timezone override
    if (timezone) {
      selectedTimezone = timezone === CustomUserTimezone.STORAGE_TIME ? this.systemTimezone : timezone;
    }

    // calculate offset between selected timezone and UTC in format +0000, -0430, +1200 etc.
    const tzDate = new Date(new Date().toLocaleString('en-US', { timeZone: selectedTimezone as string }));
    const utcDate = new Date(new Date().toLocaleString('en-US', { timeZone: 'UTC' }));
    const offsetHours = (tzDate.getTime() - utcDate.getTime()) / 60000 / 60;
    const sign = offsetHours >= 0 ? '+' : '-';
    const remainder = offsetHours % 1;

    const offsetString = `${sign}${Math.abs(Math.floor(offsetHours)).toString().padStart(2, '0')}${
      remainder ? remainder * 60 : '00'
    }`;

    const result = super.transform(value, format, offsetString, locale);
    return result?.replace(/GMT/g, 'UTC') ?? null;
  }
}
